import { type ReactElement, useState } from "react";

import {
  Box,
  Button,
  Checkbox,
  Column,
  PlusIcon,
  Popover,
  PopoverContent,
  PopoverTrigger,
  Portal,
  SearchInput,
  Skeleton,
  SkeletonBox,
  Text,
} from "@hightouch/htui";

import { useScrollFade } from "src/hooks/use-scroll-fade";
import { PROJECTS_TERMS as TERMS } from "src/pages/projects/constants";
import { useOpenProjects } from "src/pages/projects/hooks";
import { projectLabel } from "src/pages/projects/use-linked-projects";
import { CreateProjectDialog } from "src/pages/projects/components/project-properties";

// Uneven, so the placeholder rows read as titles.
const SKELETON_ROW_WIDTHS = [40, 32, 36];

export type ProjectPickerProps = {
  selectedProjectIds: string[];
  onToggle: (projectId: string, selected: boolean) => void;
  /** The button that opens the picker. It must forward its ref. */
  trigger: ReactElement;
};

/** A searchable checklist of open projects, with a way to make a new one. */
export const ProjectPicker = ({
  selectedProjectIds,
  onToggle,
  trigger,
}: ProjectPickerProps) => {
  const [isOpen, setIsOpen] = useState(false);
  const [isCreating, setIsCreating] = useState(false);
  const [search, setSearch] = useState("");
  const { projects, isLoading } = useOpenProjects();
  const scrollFade = useScrollFade();

  const selected = new Set(selectedProjectIds);
  const query = search.trim().toLowerCase();
  const matches = query
    ? projects.filter((project) => project.title.toLowerCase().includes(query))
    : projects;

  const close = () => {
    setIsOpen(false);
    setSearch("");
  };

  return (
    <>
      <Popover
        isLazy
        isOpen={isOpen}
        onOpen={() => setIsOpen(true)}
        onClose={close}
        placement="bottom-start"
      >
        <PopoverTrigger>{trigger}</PopoverTrigger>
        {/* Portalled, so a clipping container (the agent chat header) can't cut it off. */}
        <Portal>
          <PopoverContent width="sm" p={0}>
            <Column>
              <Box p={2}>
                <SearchInput
                  autoFocus
                  width="auto"
                  placeholder={`Search ${TERMS.project.plural}…`}
                  value={search}
                  onChange={(event) => setSearch(event.target.value)}
                />
              </Box>
              <Column
                ref={scrollFade.ref}
                sx={scrollFade.sx}
                maxHeight={64}
                overflowY="auto"
                px={3}
                pb={2}
                gap={2}
              >
                {isLoading ? (
                  <Skeleton isLoading>
                    <Column gap={2}>
                      {SKELETON_ROW_WIDTHS.map((width) => (
                        <SkeletonBox
                          key={width}
                          width={width}
                          height={4}
                          borderRadius="sm"
                        />
                      ))}
                    </Column>
                  </Skeleton>
                ) : matches.length === 0 ? (
                  <Box p={3} textAlign="center">
                    <Text color="text.secondary">
                      {query
                        ? `No ${TERMS.project.plural} match this search`
                        : `No ${TERMS.project.plural} yet`}
                    </Text>
                  </Box>
                ) : (
                  matches.map((project) => (
                    <Checkbox
                      key={project.id}
                      label={projectLabel(project)}
                      isChecked={selected.has(project.id)}
                      onChange={(event) =>
                        onToggle(project.id, event.target.checked)
                      }
                    />
                  ))
                )}
              </Column>
              <Box borderTopWidth="hairline" borderColor="base.divider" p={1}>
                <Button
                  variant="tertiary"
                  size="sm"
                  icon={PlusIcon}
                  onClick={() => {
                    close();
                    setIsCreating(true);
                  }}
                >
                  {`Create ${TERMS.project.singular}`}
                </Button>
              </Box>
            </Column>
          </PopoverContent>
        </Portal>
      </Popover>
      {isCreating && (
        <CreateProjectDialog
          onClose={() => setIsCreating(false)}
          // A project made from the picker is picked, and the person stays where they are.
          onCreated={(projectId) => onToggle(projectId, true)}
        />
      )}
    </>
  );
};
